import ProseFilter from '@/components/ProseFilter';
import { AnalyticsContext, OrganisationContext } from '@/context';
import { useBlockedProfileTypes } from '@/hooks';
import { Tag } from '@hogwarts/ui-components-core';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ProfilesFilterBar = ({ onFilter, initialFilters }) => {
  const { t } = useTranslation();
  const analytics = useContext(AnalyticsContext);
  const organisation = useContext(OrganisationContext);
  const { profileTypes, ratingSystems, tags } = organisation.scheme;
  const blockedProfileTypes = useBlockedProfileTypes();

  const availableProfileTypes = useMemo(() => {
    if (!blockedProfileTypes || blockedProfileTypes.length === 0) {
      return profileTypes;
    }
    return profileTypes.filter((pt) => {
      if (blockedProfileTypes.includes(pt.key)) {
        return false;
      }
      if (pt.parent) {
        const parentProfileType = organisation.scheme.getProfileType(pt.parent);
        if (parentProfileType && blockedProfileTypes.includes(parentProfileType.key)) {
          return false;
        }
      }
      return true;
    });
  }, [profileTypes, blockedProfileTypes, organisation.scheme]);

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const { ratings, ...rest } = initialFilters;
    const initialProfileTypes = rest.profileTypes || availableProfileTypes.map((p) => p.key);
    const filteredProfileTypes = initialProfileTypes.filter((ptKey) => {
      if (blockedProfileTypes?.includes(ptKey)) return false;
      const profileType = organisation.scheme.getProfileType(ptKey);
      if (profileType?.parent) {
        const parentProfileType = organisation.scheme.getProfileType(profileType.parent);
        if (parentProfileType && blockedProfileTypes?.includes(parentProfileType.key)) {
          return false;
        }
      }
      return true;
    });
    // Ensure at least one profile type is selected
    const finalProfileTypes = filteredProfileTypes.length > 0 
      ? filteredProfileTypes 
      : (availableProfileTypes.length > 0 ? [availableProfileTypes[0].key] : []);
    
    return {
      profileTypes: finalProfileTypes,
      tags: ['active', 'onboarding'],
      ...rest,
      ...(ratings &&
        ratings.reduce(
          (prev, cur) => ({
            ...prev,
            [`ratingSystem_${cur.key}`]: [cur.include],
          }),
          {}
        )),
    };
  });

  const onFiltersChanged = (filters) => {
    setSelectedFilters(filters);
    if (onFilter) {
      onFilter(filters);
    }
  };

  const terms = { prefix: t('Showing') };
  const filters = [
    {
      id: 'profileTypes',
      name: t('Profile Types'),
      available: availableProfileTypes.map((pt) => ({
        id: pt.key,
        label: pt.label,
      })),
      selected: selectedFilters.profileTypes,
      selectOptions: {
        min: 1,
        max: null,
        removable: false,
        withApply: true,
      },
      terms: {
        allSelected: t('all profile types'),
        joining: t('of type'),
        prefix: null,
        suffix: null,
      },
    },
    {
      id: 'tags',
      name: t('Tags'),
      component: Tag,
      available: tags.map((t) => ({
        id: t.key,
        label: t.label,
        color: t.color,
      })),
      selected: selectedFilters.tags,
      selectOptions: {
        min: 1,
        max: null,
        removable: false,
        withApply: true,
      },
      terms: {
        allSelected: null,
        joining: t('with'),
        prefix: null,
        suffix: t('tags'),
      },
    },
    ...ratingSystems.map((rs, idx) => ({
      id: `ratingSystem_${rs.key}`,
      name: rs.label,
      available: [
        {
          id: false,
          label: `${t('Not')} ${rs.label}`,
        },
        {
          id: true,
          label: rs.label,
        },
      ],
      selected: selectedFilters[`ratingSystem_${rs.key}`],
      selectOptions: {
        min: 1,
        max: 1,
        removable: true,
      },
      terms: {
        allSelected: null,
        joining: idx === 0 ? t('that are') : t('and'),
        prefix: null,
        suffix: null,
      },
    })),
  ];

  return (
    <ProseFilter
      terms={terms}
      filters={filters}
      activeFilters={Object.keys(selectedFilters)}
      onSelectionChange={(type, selected) => {
        const newFilters = {
          ...selectedFilters,
          [type]: selected,
        };
        analytics.events.profiles.filtersChanged({ newFilters });
        onFiltersChanged(newFilters);
      }}
      onAddFilter={(filterId) => {
        // analytics.track(analytics.trackingKeys.profiles.FILTER_ADDED, {
        //   filterId,
        // });
        const filter = filters.find((f) => f.id === filterId);
        onFiltersChanged({
          ...selectedFilters,
          [filterId]: [filter.available[0].id],
        });
      }}
      onRemoveFilter={(filterId) => {
        // analytics.track(analytics.trackingKeys.profiles.FILTER_REMOVED, {
        //   filterId,
        // });
        const newFilters = { ...selectedFilters };
        delete newFilters[filterId];
        //  activeFilters.filter((f) => f !== filterId);
        onFiltersChanged(newFilters);
      }}
    />
  );
};

export default ProfilesFilterBar;
